import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SocialShareImageField = ({ value, onChange, title, description }) => {
  const [collections, setCollections] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/content/collections`).then(r => r.json()).then(d => setCollections(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/upload/og-image`, { method: 'POST', headers: { Authorization: `Bearer ${api.getToken()}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      onChange(data.url);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const applyCover = async (url) => {
    if (!url) return;
    setBusy(true); setError('');
    try {
      const data = await api.request('/api/upload/og-image-from-url', { method: 'POST', body: JSON.stringify({ source_url: url }) });
      onChange(data.url);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const previewTitle = title || 'Crystal Media | Luxury Wedding Photography & Films';
  const previewDesc = description || 'Luxury wedding photography and cinematic films for South Asian and multicultural weddings in Toronto, Canada & USA.';

  return (
    <div className="space-y-4" data-testid="social-share-image-field">
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="og-upload-btn"
          className="px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50">
          {busy ? 'Processing…' : 'Upload image'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
        <select value={picked} onChange={(e) => { setPicked(e.target.value); applyCover(e.target.value); }} disabled={busy} data-testid="og-cover-select"
          className="px-3 py-2 border border-gray-300 rounded text-sm">
          <option value="">…or use a gallery cover</option>
          {collections.filter(c => c.cover_image_url).map(c => <option key={c.id} value={c.cover_image_url}>{c.couple} — {c.location}</option>)}
        </select>
        {value && (
          <button type="button" onClick={() => onChange('')} className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50" data-testid="og-remove-btn">Remove</button>
        )}
      </div>
      <p className="text-xs text-gray-400">Any image is auto-cropped to exactly 1200×630 (the size Facebook, Instagram, WhatsApp, iMessage, LinkedIn and Google expect).</p>
      {error && <p className="text-xs text-red-600" data-testid="og-error">{error}</p>}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">WhatsApp / iMessage / Instagram preview</p>
          <div className="max-w-sm rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm" data-testid="og-preview-card">
            <div className="aspect-[1200/630] bg-gray-100 flex items-center justify-center overflow-hidden">
              {value ? <img src={value} alt="Share preview" className="w-full h-full object-cover" data-testid="og-preview-img" /> : <span className="text-xs text-gray-400">Default image (/og-default.jpg) will be used</span>}
            </div>
            <div className="p-3">
              <p className="text-sm font-semibold text-gray-900 line-clamp-1">{previewTitle}</p>
              <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{previewDesc}</p>
              <p className="text-[11px] text-gray-400 mt-1 uppercase">crystalmedia.ca</p>
            </div>
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Google search preview</p>
          <div className="max-w-md p-3 rounded-lg border border-gray-200 bg-white" data-testid="google-preview-card">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-black text-white text-[10px] flex items-center justify-center font-bold">C</div>
              <div>
                <p className="text-xs text-gray-800 leading-none">Crystal Media</p>
                <p className="text-[11px] text-gray-500 leading-none mt-0.5">https://www.crystalmedia.ca</p>
              </div>
            </div>
            <p className="text-[#1a0dab] text-lg leading-snug line-clamp-1">{previewTitle}</p>
            <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{previewDesc}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialShareImageField;
